import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Formats Shopify cents into store currency.
   * Example: 12900 -> $129
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    const amount = cents / 100;

    const currency =
      window.Shopify?.currency?.active ||
      window.Shopify?.currency?.current ||
      'USD';

    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Forces the product page price to show the selected variant's exact price
   * after a variant is selected.
   * @param {HTMLElement} priceContainer
   * @param {any} variant
   */
  updateSelectedVariantPrice(priceContainer, variant) {
    if (!variant || typeof variant.price !== 'number') return;

    const formattedPrice = this.formatMoney(variant.price);
    const formattedCompareAtPrice =
      typeof variant.compare_at_price === 'number'
        ? this.formatMoney(variant.compare_at_price)
        : null;

    const isOnSale =
      typeof variant.compare_at_price === 'number' &&
      variant.compare_at_price > variant.price;

    const regularPrice = priceContainer.querySelector('.price__regular .price');
    const salePrice = priceContainer.querySelector('.price__sale .price-item--sale.price');
    const compareAtPrice = priceContainer.querySelector('.compare-at-price');

    const regularWrapper = priceContainer.querySelector('.price__regular');
    const saleWrapper = priceContainer.querySelector('.price__sale');

    if (isOnSale) {
      if (regularWrapper) regularWrapper.classList.add('price__hidden');
      if (saleWrapper) saleWrapper.classList.remove('price__hidden');

      if (salePrice) salePrice.textContent = formattedPrice;
      if (compareAtPrice && formattedCompareAtPrice) {
        compareAtPrice.textContent = formattedCompareAtPrice;
      }
    } else {
      if (regularWrapper) regularWrapper.classList.remove('price__hidden');
      if (saleWrapper) saleWrapper.classList.add('price__hidden');

      if (regularPrice) {
        regularPrice.textContent = formattedPrice;
      } else {
        const fallbackPrice = priceContainer.querySelector('.price');
        if (fallbackPrice) fallbackPrice.textContent = formattedPrice;
      }
    }
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);

      // Important:
      // Liquid shows the range on first page load.
      // After the customer selects a variant, JS forces the exact variant price.
      this.updateSelectedVariantPrice(newPrice, event.detail.resource);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}